import { useEffect, useRef, useState, FormEvent, MouseEvent } from "react";
import { Camera, Building2, FileCheck2, ShieldCheck, Trash2, Download, DatabaseBackup, CalendarClock } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui/PageHeader";
import { Badge } from "../components/ui/Badge";
import { redimensionarImagenABase64 } from "../lib/imagen";
import { archivoABase64 } from "../lib/archivo";
import { aplicarColorMarca } from "../lib/tema";
import { ROLES_PERSONALIZABLES, SECCIONES, type NivelPermiso, type PermisosRoles } from "../lib/permisos";
import { DomicilioCampos, type DomicilioValores } from "../components/ui/DomicilioCampos";

const domicilioVacio: DomicilioValores = {
  calle: "",
  numero: "",
  codigoPostal: "",
  colonia: "",
  ciudad: "",
  estado: "",
  pais: "México",
};

const nivelesPermiso: { value: NivelPermiso; label: string; descripcion: string }[] = [
  { value: "EDITAR", label: "Ver y editar", descripcion: "Acceso completo, como hasta ahora." },
  { value: "VER", label: "Solo ver", descripcion: "Puede consultar pero no crear, editar ni eliminar." },
  { value: "OCULTO", label: "Ocultar", descripcion: "No aparece en el menú ni se puede entrar por URL." },
];

const modulosDisponibles = [
  { key: "pos", label: "Punto de venta" },
  { key: "inventario", label: "Productos y Servicios" },
  { key: "facturacion", label: "Facturación CFDI" },
  { key: "recordatorios", label: "Recordatorios WhatsApp" },
  { key: "hospedaje", label: "Hotel / Guardería" },
  { key: "grooming", label: "Estética / Grooming" },
  { key: "entrenamiento", label: "Entrenamiento" },
  { key: "domicilio", label: "Servicio a domicilio" },
  { key: "paseos", label: "Paseos" },
];

export default function Configuracion() {
  const { tenant, soloLectura, actualizarTenant } = useAuth();
  const [nombre, setNombre] = useState("");
  const [colorPrimario, setColorPrimario] = useState("#0d9488");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [modulosActivos, setModulosActivos] = useState<string[]>([]);
  const [permisosRoles, setPermisosRoles] = useState<PermisosRoles>({});
  const [rolSeleccionado, setRolSeleccionado] = useState<string>(ROLES_PERSONALIZABLES[0].value);
  const [guardando, setGuardando] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputLogoRef = useRef<HTMLInputElement>(null);

  // --- Datos fiscales y facturación (CFDI) ---
  const [rfc, setRfc] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [regimenFiscal, setRegimenFiscal] = useState("");
  const [serieFacturas, setSerieFacturas] = useState("A");
  const [domicilioFiscal, setDomicilioFiscal] = useState<DomicilioValores>(domicilioVacio);
  const [guardandoFiscal, setGuardandoFiscal] = useState(false);
  const [mensajeFiscal, setMensajeFiscal] = useState<string | null>(null);
  const [errorFiscal, setErrorFiscal] = useState<string | null>(null);

  const [catalogos, setCatalogos] = useState<{
    regimenesFiscales: { value: string; label: string }[];
    csd: { estado: string; errorMensaje: string | null; actualizadoEn: string | null; vigenciaFin: string | null };
    facturamaConfigurado: boolean;
  } | null>(null);
  const [archivoCer, setArchivoCer] = useState<File | null>(null);
  const [archivoKey, setArchivoKey] = useState<File | null>(null);
  const [passwordCsd, setPasswordCsd] = useState("");
  const [guardandoCsd, setGuardandoCsd] = useState(false);
  const [mensajeCsd, setMensajeCsd] = useState<string | null>(null);
  const [errorCsd, setErrorCsd] = useState<string | null>(null);
  const inputCerRef = useRef<HTMLInputElement>(null);
  const inputKeyRef = useRef<HTMLInputElement>(null);

  const [respaldos, setRespaldos] = useState<{ slug: string; etiqueta: string }[]>([]);
  const [cargandoRespaldos, setCargandoRespaldos] = useState(true);

  // --- Sincronización con Google Calendar ---
  const [googleEstado, setGoogleEstado] = useState<{
    disponible: boolean;
    estado: string; // "SIN_CONFIGURAR" | "CONECTADO" | "ERROR"
    email: string | null;
    conectadoEn: string | null;
    errorMensaje: string | null;
  } | null>(null);
  const [cargandoGoogle, setCargandoGoogle] = useState(true);
  const [conectandoGoogle, setConectandoGoogle] = useState(false);
  const [desconectandoGoogle, setDesconectandoGoogle] = useState(false);
  const [mensajeGoogle, setMensajeGoogle] = useState<string | null>(null);
  const [errorGoogle, setErrorGoogle] = useState<string | null>(null);
  const [errorCargaRespaldos, setErrorCargaRespaldos] = useState<string | null>(null);
  const [descargandoSlug, setDescargandoSlug] = useState<string | null>(null);
  const [errorRespaldo, setErrorRespaldo] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setNombre(tenant.nombre ?? "");
      setColorPrimario(tenant.colorPrimario ?? "#0d9488");
      setLogoUrl(tenant.logoUrl ?? null);
      setModulosActivos(tenant.modulosActivos ?? []);
      setPermisosRoles((tenant.permisosRoles as PermisosRoles) ?? {});
    }
  }, [tenant]);

  const cargarDatosFiscales = async () => {
    const [resTenant, resCatalogos] = await Promise.all([api.get("/tenants/me"), api.get("/facturacion/catalogos")]);
    const t = resTenant.data;
    setRfc(t.rfc ?? "");
    setRazonSocial(t.razonSocial ?? "");
    setRegimenFiscal(t.regimenFiscal ?? "");
    setSerieFacturas(t.serieFacturas ?? "A");
    setDomicilioFiscal({
      calle: t.calle ?? "",
      numero: t.numero ?? "",
      codigoPostal: t.codigoPostal ?? "",
      colonia: t.colonia ?? "",
      ciudad: t.ciudad ?? "",
      estado: t.estadoDireccion ?? "",
      pais: t.pais ?? "México",
    });
    setCatalogos(resCatalogos.data);
  };

  useEffect(() => {
    cargarDatosFiscales().catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/respaldo")
      .then((res) => setRespaldos(res.data.items))
      .catch((err) => setErrorCargaRespaldos(mensajeError(err)))
      .finally(() => setCargandoRespaldos(false));
  }, []);

  const cargarEstadoGoogle = () => {
    api
      .get("/google-calendar/estado")
      .then((res) => setGoogleEstado(res.data))
      .catch((err) => setErrorGoogle(mensajeError(err)))
      .finally(() => setCargandoGoogle(false));
  };

  useEffect(() => {
    cargarEstadoGoogle();

    // Cuando Google regresa del flujo de conexión, redirige aquí con
    // ?google=conectado|cancelado|error (ver googleCalendarCallback.routes.ts
    // en el backend). Mostramos el resultado y limpiamos el parámetro para
    // que el mensaje no reaparezca si el negocio recarga la página.
    const params = new URLSearchParams(window.location.search);
    const resultado = params.get("google");
    if (resultado) {
      if (resultado === "conectado") setMensajeGoogle("Tu cuenta de Google quedó conectada correctamente.");
      else if (resultado === "cancelado") setErrorGoogle("Cancelaste la conexión con Google, no se guardó ningún cambio.");
      else if (resultado === "error") setErrorGoogle("No se pudo completar la conexión con Google. Intenta de nuevo.");
      params.delete("google");
      const nuevaUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", nuevaUrl);
    }
  }, []);

  async function conectarGoogle() {
    if (soloLectura) return;
    setConectandoGoogle(true);
    setErrorGoogle(null);
    setMensajeGoogle(null);
    try {
      const res = await api.post("/google-calendar/conectar");
      // Navegación de página completa a la pantalla de consentimiento de
      // Google — no es una llamada de API normal, por eso no se usa axios.
      window.location.href = res.data.url;
    } catch (err) {
      setErrorGoogle(mensajeError(err));
      setConectandoGoogle(false);
    }
  }

  async function desconectarGoogle() {
    if (soloLectura) return;
    if (!confirm("¿Seguro que quieres desconectar Google Calendar? Las citas nuevas ya no se van a reflejar en tu calendario de Google.")) return;
    setDesconectandoGoogle(true);
    setErrorGoogle(null);
    setMensajeGoogle(null);
    try {
      await api.post("/google-calendar/desconectar");
      setMensajeGoogle("Se desconectó tu cuenta de Google correctamente.");
      cargarEstadoGoogle();
    } catch (err) {
      setErrorGoogle(mensajeError(err));
    } finally {
      setDesconectandoGoogle(false);
    }
  }

  // Descarga el .xlsx de un módulo: el endpoint requiere el token de sesión
  // (igual que cualquier otra ruta del panel), así que no puede ser un link
  // directo — se pide como blob autenticado y se dispara la descarga desde
  // el navegador con un enlace temporal invisible.
  async function descargarRespaldo(slug: string) {
    setDescargandoSlug(slug);
    setErrorRespaldo(null);
    try {
      const res = await api.get(`/respaldo/${slug}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `${slug}.xlsx`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorRespaldo(mensajeError(err));
    } finally {
      setDescargandoSlug(null);
    }
  }

  async function guardarDatosFiscales(e: FormEvent) {
    e.preventDefault();
    if (soloLectura) return;
    setGuardandoFiscal(true);
    setMensajeFiscal(null);
    setErrorFiscal(null);
    try {
      await api.put("/tenants/me", {
        rfc,
        razonSocial,
        regimenFiscal,
        serieFacturas,
        calle: domicilioFiscal.calle,
        numero: domicilioFiscal.numero,
        codigoPostal: domicilioFiscal.codigoPostal,
        colonia: domicilioFiscal.colonia,
        ciudad: domicilioFiscal.ciudad,
        estadoDireccion: domicilioFiscal.estado,
        pais: domicilioFiscal.pais,
      });
      setMensajeFiscal("Datos fiscales guardados correctamente.");
    } catch (err) {
      setErrorFiscal(mensajeError(err));
    } finally {
      setGuardandoFiscal(false);
    }
  }

  async function guardarCsd(e: FormEvent) {
    e.preventDefault();
    if (soloLectura) return;
    if (!archivoCer || !archivoKey || !passwordCsd) {
      setErrorCsd("Sube el archivo .cer, el archivo .key, y escribe la contraseña de tu certificado.");
      return;
    }
    setGuardandoCsd(true);
    setMensajeCsd(null);
    setErrorCsd(null);
    try {
      const [certificadoBase64, llaveBase64] = await Promise.all([archivoABase64(archivoCer), archivoABase64(archivoKey)]);
      await api.post("/facturacion/csd", { certificadoBase64, llaveBase64, password: passwordCsd });
      setMensajeCsd("Certificado guardado y validado correctamente. Ya puedes timbrar facturas.");
      setArchivoCer(null);
      setArchivoKey(null);
      setPasswordCsd("");
      if (inputCerRef.current) inputCerRef.current.value = "";
      if (inputKeyRef.current) inputKeyRef.current.value = "";
      await cargarDatosFiscales();
    } catch (err) {
      setErrorCsd(mensajeError(err));
    } finally {
      setGuardandoCsd(false);
    }
  }

  async function eliminarCsd() {
    if (soloLectura) return;
    if (!confirm("¿Seguro que quieres quitar tu certificado de sello digital? No podrás timbrar facturas hasta subir uno nuevo.")) return;
    try {
      await api.delete("/facturacion/csd");
      await cargarDatosFiscales();
    } catch (err) {
      setErrorCsd(mensajeError(err));
    }
  }

  function cambiarPermiso(seccion: string, nivel: NivelPermiso) {
    if (soloLectura) return;
    setPermisosRoles((prev) => ({
      ...prev,
      [rolSeleccionado]: { ...prev[rolSeleccionado], [seccion]: nivel },
    }));
  }

  function toggleModulo(key: string) {
    if (soloLectura) return;
    setModulosActivos((prev) => (prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]));
  }

  async function elegirLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo || soloLectura) return;
    setSubiendoLogo(true);
    setError(null);
    try {
      const base64 = await redimensionarImagenABase64(archivo, 400);
      setLogoUrl(base64);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setSubiendoLogo(false);
    }
  }

  async function guardarCambios(e: FormEvent | MouseEvent) {
    e.preventDefault();
    if (soloLectura) return;
    setGuardando(true);
    setMensaje(null);
    setError(null);
    try {
      await api.put("/tenants/me", { nombre, colorPrimario, logoUrl, modulosActivos, permisosRoles });
      actualizarTenant({ nombre, colorPrimario, logoUrl, modulosActivos, permisosRoles });
      setMensaje("Cambios guardados correctamente.");
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader titulo="Configuración" descripcion="Datos generales y módulos activos de tu negocio." icono={<Building2 size={20} />} />

      <form onSubmit={guardarCambios} className="card p-6 space-y-6 max-w-2xl">
        <div>
          <label className="label mb-2 block">Logo del negocio</label>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo del negocio" className="h-full w-full object-cover" />
              ) : (
                <Building2 size={28} className="text-slate-300" />
              )}
            </div>
            <div>
              <input ref={inputLogoRef} type="file" accept="image/*" className="hidden" onChange={elegirLogo} disabled={soloLectura} />
              <button
                type="button"
                className="btn-secondary"
                disabled={soloLectura || subiendoLogo}
                onClick={() => inputLogoRef.current?.click()}
              >
                <Camera size={16} /> {subiendoLogo ? "Procesando..." : logoUrl ? "Cambiar imagen" : "Subir imagen"}
              </button>
              {logoUrl && !soloLectura && (
                <button type="button" className="ml-2 text-xs text-red-500 hover:underline" onClick={() => setLogoUrl(null)}>
                  Quitar
                </button>
              )}
              <p className="mt-1 text-xs text-slate-400">JPG o PNG. Se ajusta automáticamente al tamaño correcto.</p>
            </div>
          </div>
        </div>
        <div>
          <label className="label">Nombre del negocio</label>
          <input className="input" disabled={soloLectura} value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </div>
        <div>
          <label className="label">Color principal</label>
          <input
            type="color"
            disabled={soloLectura}
            className="h-10 w-20 rounded-lg border border-slate-200"
            value={colorPrimario}
            onChange={(e) => {
              setColorPrimario(e.target.value);
              // Vista previa inmediata mientras se elige, antes de guardar.
              aplicarColorMarca(e.target.value);
            }}
          />
          <p className="mt-1 text-xs text-slate-400">Se usa en botones y acentos de tu panel. Guarda los cambios para que quede fijo.</p>
        </div>
        <div>
          <label className="label mb-2 block">Módulos activos</label>
          <div className="grid grid-cols-2 gap-2">
            {modulosDisponibles.map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  disabled={soloLectura}
                  checked={modulosActivos.includes(m.key)}
                  onChange={() => toggleModulo(m.key)}
                />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        {mensaje && <p className="text-sm text-green-600">{mensaje}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          className="btn-primary"
          disabled={soloLectura || guardando}
          title={soloLectura ? "El negocio está en modo de solo lectura" : undefined}
        >
          {guardando ? "Guardando..." : "Guardar cambios"}
        </button>
      </form>

      <form onSubmit={guardarDatosFiscales} className="card p-6 mt-6 space-y-6 max-w-2xl">
        <div>
          <label className="label mb-1 block">Datos fiscales</label>
          <p className="mb-4 text-xs text-slate-400">
            Necesarios para poder timbrar CFDI 4.0 ante el SAT. Deben coincidir exactamente con tu Constancia de
            Situación Fiscal.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">RFC</label>
            <input
              className="input uppercase"
              disabled={soloLectura}
              value={rfc}
              onChange={(e) => setRfc(e.target.value.toUpperCase())}
              placeholder="Ej. VECJ880326XXX"
            />
          </div>
          <div>
            <label className="label">Régimen fiscal</label>
            <select className="input" disabled={soloLectura} value={regimenFiscal} onChange={(e) => setRegimenFiscal(e.target.value)}>
              <option value="">Selecciona...</option>
              {(catalogos?.regimenesFiscales ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Razón social (para facturar)</label>
            <input
              className="input uppercase"
              disabled={soloLectura}
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value.toUpperCase())}
              placeholder="Tal como aparece en tu Constancia de Situación Fiscal"
            />
            <p className="mt-1 text-xs text-slate-400">
              Escríbela EXACTAMENTE como está en tu Constancia de Situación Fiscal del SAT (incluyendo "S.A. DE C.V.",
              "S. DE R.L. DE C.V.", etc. si así aparece). Es distinta del nombre comercial de tu negocio y el SAT
              rechaza la factura si no coincide al pie de la letra.
            </p>
          </div>
        </div>

        <div>
          <label className="label mb-2 block">Domicilio fiscal</label>
          <DomicilioCampos
            valores={domicilioFiscal}
            onChange={(campo, valor) => setDomicilioFiscal((prev) => ({ ...prev, [campo]: valor }))}
            disabled={soloLectura}
          />
        </div>

        <div>
          <label className="label">Serie de facturas</label>
          <input
            className="input max-w-[120px]"
            disabled={soloLectura}
            value={serieFacturas}
            onChange={(e) => setSerieFacturas(e.target.value.toUpperCase().slice(0, 10))}
            placeholder="A"
          />
          <p className="mt-1 text-xs text-slate-400">Aparece antes del folio en cada factura (ej. "A-102").</p>
        </div>

        {mensajeFiscal && <p className="text-sm text-green-600">{mensajeFiscal}</p>}
        {errorFiscal && <p className="text-sm text-red-600">{errorFiscal}</p>}
        <button type="submit" className="btn-primary" disabled={soloLectura || guardandoFiscal}>
          {guardandoFiscal ? "Guardando..." : "Guardar datos fiscales"}
        </button>
      </form>

      <div className="card p-6 mt-6 max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <label className="label mb-1 block">Certificado de sello digital (CSD)</label>
            <p className="text-xs text-slate-400">
              El mismo certificado que usas para facturar desde el SAT o desde otro sistema. Se guarda cifrado y se
              usa exclusivamente para timbrar tus propias facturas — nunca se comparte con otros negocios.
            </p>
          </div>
          {catalogos && (
            <Badge color={catalogos.csd.estado === "VALIDO" ? "verde" : catalogos.csd.estado === "ERROR" ? "rojo" : "gris"}>
              {catalogos.csd.estado === "VALIDO" ? "Configurado" : catalogos.csd.estado === "ERROR" ? "Con error" : "Sin configurar"}
            </Badge>
          )}
        </div>

        {!catalogos?.facturamaConfigurado && (
          <p className="mb-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">
            El proveedor de timbrado todavía no está configurado en el servidor. Puedes cargar tu certificado, pero no
            vas a poder timbrar hasta que el administrador de la plataforma configure Facturama.
          </p>
        )}

        {catalogos?.csd.estado === "ERROR" && catalogos.csd.errorMensaje && (
          <p className="mb-4 rounded-xl bg-red-50 p-3 text-xs text-red-700">{catalogos.csd.errorMensaje}</p>
        )}

        {catalogos?.csd.estado === "VALIDO" && catalogos.csd.vigenciaFin && (
          <p
            className={`mb-4 rounded-xl p-3 text-xs ${
              new Date(catalogos.csd.vigenciaFin).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000
                ? "bg-amber-50 text-amber-700"
                : "bg-slate-50 text-slate-500"
            }`}
          >
            Este certificado es válido hasta el {new Date(catalogos.csd.vigenciaFin).toLocaleDateString("es-MX")}.
            {new Date(catalogos.csd.vigenciaFin).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000 &&
              " Está por vencer — tramita uno nuevo en el SAT para no interrumpir tu facturación."}
          </p>
        )}

        <form onSubmit={guardarCsd} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-1.5">
                <FileCheck2 size={14} /> Archivo .cer
              </label>
              <input
                ref={inputCerRef}
                type="file"
                accept=".cer"
                disabled={soloLectura}
                className="input !py-1.5 text-xs"
                onChange={(e) => setArchivoCer(e.target.files?.[0] ?? null)}
              />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                <FileCheck2 size={14} /> Archivo .key
              </label>
              <input
                ref={inputKeyRef}
                type="file"
                accept=".key"
                disabled={soloLectura}
                className="input !py-1.5 text-xs"
                onChange={(e) => setArchivoKey(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <div>
            <label className="label">Contraseña de la llave privada</label>
            <input
              type="password"
              className="input max-w-xs"
              disabled={soloLectura}
              value={passwordCsd}
              onChange={(e) => setPasswordCsd(e.target.value)}
            />
          </div>

          {mensajeCsd && <p className="text-sm text-green-600">{mensajeCsd}</p>}
          {errorCsd && <p className="text-sm text-red-600">{errorCsd}</p>}

          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={soloLectura || guardandoCsd}>
              <ShieldCheck size={16} /> {guardandoCsd ? "Validando..." : "Guardar certificado"}
            </button>
            {catalogos?.csd.estado === "VALIDO" && (
              <button type="button" className="btn-secondary text-red-600" disabled={soloLectura} onClick={eliminarCsd}>
                <Trash2 size={16} /> Quitar certificado
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card p-6 mt-6 max-w-2xl">
        <label className="label mb-1 block">Roles y permisos</label>
        <p className="mb-4 text-xs text-slate-400">
          Decide qué puede ver y hacer cada rol en el panel. El Administrador siempre tiene acceso completo a todo,
          para que el negocio nunca se quede bloqueado por accidente. Una sección sin cambios se comporta como
          "Ver y editar" (igual que siempre).
        </p>

        <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-100 pb-3">
          {ROLES_PERSONALIZABLES.map((r) => (
            <button
              key={r.value}
              type="button"
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                rolSeleccionado === r.value ? "bg-brand-50 text-brand-700" : "text-slate-500 hover:bg-slate-50"
              }`}
              onClick={() => setRolSeleccionado(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          {SECCIONES.map((s) => {
            const nivelActual = permisosRoles[rolSeleccionado]?.[s.value] ?? "EDITAR";
            return (
              <div key={s.value} className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 hover:bg-slate-50">
                <span className="text-sm text-slate-600">{s.label}</span>
                <select
                  className="input !w-auto !py-1.5 text-sm"
                  disabled={soloLectura}
                  value={nivelActual}
                  onChange={(e) => cambiarPermiso(s.value, e.target.value as NivelPermiso)}
                >
                  {nivelesPermiso.map((n) => (
                    <option key={n.value} value={n.value} title={n.descripcion}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {mensaje && <p className="mt-4 text-sm text-green-600">{mensaje}</p>}
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          type="button"
          className="btn-primary mt-4"
          disabled={soloLectura || guardando}
          title={soloLectura ? "El negocio está en modo de solo lectura" : undefined}
          onClick={guardarCambios}
        >
          {guardando ? "Guardando..." : "Guardar permisos"}
        </button>
      </div>

      <div className="card p-6 mt-6 max-w-2xl">
        <label className="label mb-1 flex items-center gap-2">
          <DatabaseBackup size={15} /> Respaldo de información
        </label>
        <p className="mb-4 text-xs text-slate-400">
          Descarga en Excel todo lo que tienes cargado en cada módulo, para guardar tu propia copia cuando quieras.
          Cada archivo trae únicamente la información de tu negocio.
        </p>

        {errorRespaldo && <p className="mb-3 text-sm text-red-600">{errorRespaldo}</p>}

        {cargandoRespaldos ? (
          <p className="text-sm text-slate-400">Cargando módulos disponibles...</p>
        ) : errorCargaRespaldos ? (
          <p className="text-sm text-red-600">
            No se pudo cargar la lista de respaldos: {errorCargaRespaldos}
          </p>
        ) : respaldos.length === 0 ? (
          <p className="text-sm text-slate-400">No hay módulos de respaldo disponibles.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {respaldos.map((r) => (
              <button
                key={r.slug}
                type="button"
                onClick={() => descargarRespaldo(r.slug)}
                disabled={descargandoSlug === r.slug}
                className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2.5 text-left text-sm text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 disabled:opacity-50"
              >
                <span>{r.etiqueta}</span>
                <Download size={15} className={descargandoSlug === r.slug ? "animate-pulse" : ""} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card p-6 mt-6 max-w-2xl">
        <div className="mb-1 flex items-center justify-between gap-3">
          <label className="label flex items-center gap-2">
            <CalendarClock size={15} /> Sincronizar con Google Calendar
          </label>
          {googleEstado?.disponible && !cargandoGoogle && (
            <Badge color={googleEstado.estado === "CONECTADO" ? "verde" : googleEstado.estado === "ERROR" ? "rojo" : "gris"}>
              {googleEstado.estado === "CONECTADO" ? "Conectado" : googleEstado.estado === "ERROR" ? "Con error" : "Sin conectar"}
            </Badge>
          )}
        </div>
        <p className="mb-4 text-xs text-slate-400">
          Conecta tu cuenta de Gmail para ver las citas de tu Agenda directamente en tu calendario de Google (en la
          computadora o el celular). Se crea un calendario NUEVO y separado dentro de tu cuenta — tus citas del
          negocio no se mezclan con tus eventos personales, y lo puedes mostrar u ocultar cuando quieras desde
          Google Calendar.
        </p>

        {mensajeGoogle && <p className="mb-3 text-sm text-emerald-600">{mensajeGoogle}</p>}
        {errorGoogle && <p className="mb-3 text-sm text-red-600">{errorGoogle}</p>}

        {cargandoGoogle ? (
          <p className="text-sm text-slate-400">Cargando...</p>
        ) : !googleEstado?.disponible ? (
          <p className="text-sm text-slate-400">
            Esta función todavía no está disponible en tu servidor. Contacta a soporte@mascotas.vet.
          </p>
        ) : googleEstado.estado === "CONECTADO" ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600">
              {googleEstado.email ? (
                <>
                  Conectado como <span className="font-medium">{googleEstado.email}</span>
                </>
              ) : (
                "Conectado correctamente"
              )}
            </p>
            <button
              type="button"
              onClick={desconectarGoogle}
              disabled={soloLectura || desconectandoGoogle}
              className="btn-secondary text-red-600"
            >
              {desconectandoGoogle ? "Desconectando..." : "Desconectar"}
            </button>
          </div>
        ) : (
          <div>
            {googleEstado.estado === "ERROR" && googleEstado.errorMensaje && (
              <p className="mb-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">{googleEstado.errorMensaje}</p>
            )}
            <button
              type="button"
              onClick={conectarGoogle}
              disabled={soloLectura || conectandoGoogle}
              className="btn-primary"
            >
              {conectandoGoogle ? "Conectando..." : "Conectar con Google"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
